import React, { useState, useEffect, useMemo } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { LOAD_RATIO_RISK_BANDS, getLoadRatioRiskBand, RPE_INTENSITY_RANGES } from '../../../utils/trainingLoadCalculations';

// Hooper Index color classification (matching HooperIndexDisplay.jsx)
const getHooperColor = (score) => {
  if (score <= 12) return '#22C55E';
  if (score <= 16) return '#3B82F6';
  if (score <= 20) return '#F59E0B';
  return '#EF4444';
};

const getHooperCategory = (score) => {
  if (score <= 12) return 'Excellent';
  if (score <= 16) return 'Good';
  if (score <= 20) return 'Fair';
  return 'Poor';
};

// Readiness Index color classification (matching existing thresholds)
const getReadinessColor = (readinessIndex) => {
  if (readinessIndex < 0.33) return '#22C55E'; // Green
  if (readinessIndex < 0.66) return '#F59E0B'; // Amber
  return '#EF4444'; // Red
};

const getReadinessStatus = (readinessIndex) => {
  if (readinessIndex < 0.33) return 'Good';
  if (readinessIndex < 0.66) return 'Caution';
  return 'High Risk';
};

const LoadProgressionChart = ({ data, dateRange, wellnessData = [], readinessData = [] }) => {
  const [showWellness, setShowWellness] = useState(() => {
    // Load toggle state from localStorage
    const saved = localStorage.getItem('showWellnessOverlay');
    return saved === 'true';
  });

  const [showReadiness, setShowReadiness] = useState(() => {
    // Load toggle state from localStorage
    const saved = localStorage.getItem('showReadinessOverlay');
    return saved === 'true';
  });

  // Persist toggle state to localStorage
  useEffect(() => {
    localStorage.setItem('showWellnessOverlay', showWellness?.toString());
  }, [showWellness]);

  useEffect(() => {
    localStorage.setItem('showReadinessOverlay', showReadiness?.toString());
  }, [showReadiness]);

  // Clean up old view mode from localStorage
  useEffect(() => {
    localStorage.removeItem('loadChartViewMode');
  }, []);

  // Merge wellness and readiness data with daily chart data
  const mergedDailyData = useMemo(() => {
    let mergedData = data;

    // Merge wellness data
    if (showWellness && wellnessData && wellnessData?.length > 0) {
      const wellnessMap = {};
      wellnessData?.forEach(entry => {
        if (entry?.date && entry?.hooperIndex !== undefined) {
          wellnessMap[entry.date] = entry?.hooperIndex;
        }
      });

      mergedData = mergedData?.map(item => ({
        ...item,
        wellness: wellnessMap?.[item?.date] || null
      }));
    }

    // Merge readiness data
    if (showReadiness && readinessData && readinessData?.length > 0) {
      const readinessMap = {};
      readinessData?.forEach(entry => {
        if (entry?.date && entry?.readinessIndex !== undefined && entry?.readinessIndex !== null) {
          readinessMap[entry.date] = entry?.readinessIndex;
        }
      });

      mergedData = mergedData?.map(item => ({
        ...item,
        readiness: readinessMap?.[item?.date] || null
      }));
    }

    return mergedData;
  }, [data, wellnessData, showWellness, readinessData, showReadiness]);

  const getRiskBandColor = (ratio) => {
    const riskBand = getLoadRatioRiskBand(ratio);
    return riskBand?.color;
  };

  const getRiskBandLabel = (ratio) => {
    const riskBand = getLoadRatioRiskBand(ratio);
    return riskBand?.label;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      // Calculate total load from RPE segments
      const lowLoad = payload?.find(entry => entry?.dataKey === 'lowLoad')?.value || 0;
      const moderateLoad = payload?.find(entry => entry?.dataKey === 'moderateLoad')?.value || 0;
      const highLoad = payload?.find(entry => entry?.dataKey === 'highLoad')?.value || 0;
      const totalLoad = lowLoad + moderateLoad + highLoad;
      
      // Get wellness value if available
      const wellnessValue = payload?.find(entry => entry?.dataKey === 'wellness')?.value;
      
      // Get readiness value if available
      const readinessValue = payload?.find(entry => entry?.dataKey === 'readiness')?.value;
      
      // Get load ratio
      const loadRatioData = payload?.find(entry => entry?.dataKey === 'displayRatio');
      
      return (
        <div className="bg-surface border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-text-primary mb-2">{label}</p>
          {/* Total Load */}
          <div className="mb-2 pb-2 border-b border-border">
            <div className="flex items-center justify-between text-xs">
              <span className="text-text-secondary">Total Load:</span>
              <span className="font-medium text-text-primary">{totalLoad?.toFixed(1)} AU</span>
            </div>
          </div>
          {/* RPE Intensity Breakdown */}
          <div className="space-y-1 mb-2 pb-2 border-b border-border">
            <p className="text-xs font-medium text-text-secondary mb-1">Intensity Breakdown:</p>
            {highLoad > 0 && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-sm" 
                    style={{ backgroundColor: RPE_INTENSITY_RANGES?.HIGH?.color }}
                  />
                  <span className="text-text-secondary">{RPE_INTENSITY_RANGES?.HIGH?.label}:</span>
                </div>
                <span className="font-medium text-text-primary">{highLoad?.toFixed(1)} AU</span>
              </div>
            )}
            {moderateLoad > 0 && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-sm" 
                    style={{ backgroundColor: RPE_INTENSITY_RANGES?.MODERATE?.color }}
                  />
                  <span className="text-text-secondary">{RPE_INTENSITY_RANGES?.MODERATE?.label}:</span>
                </div>
                <span className="font-medium text-text-primary">{moderateLoad?.toFixed(1)} AU</span>
              </div>
            )}
            {lowLoad > 0 && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-sm" 
                    style={{ backgroundColor: RPE_INTENSITY_RANGES?.LOW?.color }}
                  />
                  <span className="text-text-secondary">{RPE_INTENSITY_RANGES?.LOW?.label}:</span>
                </div>
                <span className="font-medium text-text-primary">{lowLoad?.toFixed(1)} AU</span>
              </div>
            )}
          </div>
          {/* Load Ratio with Category */}
          {loadRatioData && (
            <div className="mb-2">
              {!loadRatioData?.value ? (
                <div className="flex items-center space-x-2 text-xs">
                  <div className="w-3 h-3 rounded-full bg-gray-400" />
                  <span className="text-text-secondary">Load Ratio:</span>
                  <span className="font-medium text-text-secondary">
                    Insufficient chronic window
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: getRiskBandColor(loadRatioData?.value) }}
                    />
                    <span className="text-text-secondary">Load Ratio:</span>
                  </div>
                  <span className="font-medium text-text-primary">
                    {loadRatioData?.value?.toFixed(2)} ({getRiskBandLabel(loadRatioData?.value)})
                  </span>
                </div>
              )}
            </div>
          )}
          {/* Wellness */}
          {showWellness && wellnessValue !== undefined && wellnessValue !== null && (
            <div className="flex items-center justify-between text-xs pt-2 border-t border-border">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#8B5CF6' }} />
                <span className="text-text-secondary">Wellness:</span>
              </div>
              <span className="font-medium text-text-primary">{wellnessValue?.toFixed(0)} / 28</span>
            </div>
          )}
          {/* Readiness */}
          {showReadiness && readinessValue !== undefined && readinessValue !== null && (
            <div className="flex items-center justify-between text-xs pt-2 border-t border-border">
              <div className="flex items-center space-x-2">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: getReadinessColor(readinessValue) }}
                />
                <span className="text-text-secondary">Readiness:</span>
              </div>
              <span className="font-medium text-text-primary">
                {readinessValue?.toFixed(2)} ({getReadinessStatus(readinessValue)})
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Process data to handle null ratios
  const processedData = mergedDailyData?.map(item => ({
    ...item,
    displayRatio: item?.loadRatio
  }));

  // Calculate date range duration in days
  const getDateRangeDays = () => {
    if (dateRange?.preset === 'custom') {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      return Math.ceil((end - start) / (24 * 60 * 60 * 1000)) + 1;
    }
    return parseInt(dateRange?.preset || '30');
  };

  const dateRangeDays = getDateRangeDays();
  const needsScrolling = dateRangeDays > 60;

  // Calculate chart width for scrolling
  const getChartWidth = () => {
    if (needsScrolling) {
      return dateRangeDays * 15; // ~15px per day
    }
    return '100%';
  };

  // Calculate left margin based on readiness overlay
  const getLeftMargin = () => {
    return showReadiness ? 55 : 10;
  };

  // Calculate right margin based on wellness overlay
  const getRightMargin = () => {
    return showWellness ? 55 : 10;
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Training Load Progression</h3>
        </div>
        <div className="flex items-center space-x-4">
          {/* Wellness Toggle */}
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showWellness}
              onChange={(e) => setShowWellness(e?.target?.checked)}
              className="w-4 h-4 text-primary bg-surface border-border rounded focus:ring-primary focus:ring-2"
            />
            <span className="text-xs text-text-secondary">Show wellness overlay</span>
          </label>
          {/* Readiness Toggle */}
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showReadiness}
              onChange={(e) => setShowReadiness(e?.target?.checked)}
              className="w-4 h-4 text-primary bg-surface border-border rounded focus:ring-primary focus:ring-2"
            />
            <span className="text-xs text-text-secondary">Show readiness overlay</span>
          </label>
          {/* Legend */}
          <div className="flex items-center space-x-4 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: RPE_INTENSITY_RANGES?.LOW?.color }} />
              <span className="text-text-secondary">Low (RPE 1-3)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: RPE_INTENSITY_RANGES?.MODERATE?.color }} />
              <span className="text-text-secondary">Moderate (RPE 4-6)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: RPE_INTENSITY_RANGES?.HIGH?.color }} />
              <span className="text-text-secondary">Hard (RPE 7-10)</span>
            </div>
            {showWellness && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-0.5 rounded-sm" style={{ backgroundColor: '#8B5CF6' }} />
                <span className="text-text-secondary">Wellness</span>
              </div>
            )}
            {showReadiness && (
              <div className="flex items-center space-x-2">
                <div className="w-3 h-0.5 rounded-sm" style={{ backgroundColor: '#10B981' }} />
                <span className="text-text-secondary">Readiness</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div 
        className="h-80" 
        style={{ 
          overflowX: needsScrolling ? 'auto' : 'visible',
          overflowY: 'hidden'
        }}
      >
        <div style={{ width: getChartWidth(), minWidth: needsScrolling ? 'auto' : '100%' }}>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={processedData} margin={{ top: 5, right: getRightMargin(), left: getLeftMargin(), bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="date" 
                stroke="var(--color-text-secondary)"
                fontSize={12}
              />
              <YAxis 
                yAxisId="load"
                stroke="var(--color-text-secondary)"
                fontSize={12}
                label={{ value: 'Load (AU)', angle: -90, position: 'insideLeft', dx: 14, style: { fontSize: 12, textAnchor: 'middle' } }}
              />
              {showReadiness && (
                <YAxis 
                  yAxisId="readiness"
                  orientation="left"
                  stroke="#10B981"
                  fontSize={12}
                  domain={[0, 1]}
                  ticks={[0, 0.5, 1]}
                  tickFormatter={(value) => value}
                  width={30}
                />
              )}
              <YAxis 
                yAxisId="ratio"
                orientation="right"
                stroke="var(--color-text-secondary)"
                fontSize={12}
                domain={[0, 2]}
                ticks={[0, 0.5, 1, 1.5, 2]}
                tickFormatter={(value) => value}
                label={{ value: 'ACWR', angle: 90, position: 'insideRight', dx: -14, style: { fontSize: 12, textAnchor: 'middle' } }}
              />
              {showWellness && (
                <YAxis 
                  yAxisId="wellness"
                  orientation="right"
                  stroke="#8B5CF6"
                  fontSize={12}
                  domain={[4, 28]}
                  ticks={[4, 12, 20, 28]}
                  width={30}
                />
              )}
              <Tooltip content={<CustomTooltip />} />
              
              {/* Stacked Bars for RPE Intensity Breakdown */}
              <Bar
                yAxisId="load"
                dataKey="lowLoad"
                stackId="dailyLoad"
                fill={RPE_INTENSITY_RANGES?.LOW?.color}
                name="Low Intensity"
              />
              <Bar
                yAxisId="load"
                dataKey="moderateLoad"
                stackId="dailyLoad"
                fill={RPE_INTENSITY_RANGES?.MODERATE?.color}
                name="Moderate Intensity"
              />
              <Bar
                yAxisId="load"
                dataKey="highLoad"
                stackId="dailyLoad"
                fill={RPE_INTENSITY_RANGES?.HIGH?.color}
                name="High Intensity"
              />
              
              {/* Load Ratio as Line with Dynamic Color */}
              <Line
                yAxisId="ratio"
                type="monotone"
                dataKey="displayRatio"
                strokeWidth={3}
                name="Load Ratio"
                connectNulls={false}
                stroke="#9CA3AF"
                dot={(props) => {
                  if (!props?.payload?.displayRatio) return null;
                  const color = getRiskBandColor(props?.payload?.displayRatio);
                  return (
                    <circle 
                      cx={props?.cx} 
                      cy={props?.cy} 
                      r={4} 
                      fill={color}
                      strokeWidth={2}
                      stroke={color}
                    />
                  );
                }}
                activeDot={(props) => {
                  if (!props?.payload?.displayRatio) return null;
                  const color = getRiskBandColor(props?.payload?.displayRatio);
                  return (
                    <circle 
                      cx={props?.cx} 
                      cy={props?.cy} 
                      r={6} 
                      fill={color}
                      strokeWidth={2}
                      stroke={color}
                    />
                  );
                }}
              />
              
              {/* Wellness Line (Hooper Index) */}
              {showWellness && (
                <Line
                  yAxisId="wellness"
                  type="monotone"
                  dataKey="wellness"
                  strokeWidth={2}
                  name="Wellness (Hooper Index)"
                  connectNulls={false}
                  stroke="#8B5CF6"
                  dot={(props) => {
                    if (!props?.payload?.wellness) return null;
                    const color = getHooperColor(props?.payload?.wellness);
                    return (
                      <circle 
                        cx={props?.cx} 
                        cy={props?.cy} 
                        r={4} 
                        fill={color}
                        strokeWidth={2}
                        stroke={color}
                      />
                    );
                  }}
                  activeDot={(props) => {
                    if (!props?.payload?.wellness) return null;
                    const color = getHooperColor(props?.payload?.wellness);
                    return (
                      <circle 
                        cx={props?.cx} 
                        cy={props?.cy} 
                        r={6} 
                        fill={color}
                        strokeWidth={2}
                        stroke={color}
                      />
                    );
                  }}
                />
              )}
              
              {/* Readiness Line */}
              {showReadiness && (
                <Line
                  yAxisId="readiness"
                  type="monotone"
                  dataKey="readiness"
                  strokeWidth={2}
                  name="Readiness Index"
                  connectNulls={false}
                  stroke="#10B981"
                  dot={(props) => {
                    if (!props?.payload?.readiness && props?.payload?.readiness !== 0) return null;
                    const color = getReadinessColor(props?.payload?.readiness);
                    return (
                      <circle 
                        cx={props?.cx} 
                        cy={props?.cy} 
                        r={4} 
                        fill={color}
                        strokeWidth={2}
                        stroke={color}
                      />
                    );
                  }}
                  activeDot={(props) => {
                    if (!props?.payload?.readiness && props?.payload?.readiness !== 0) return null;
                    const color = getReadinessColor(props?.payload?.readiness);
                    return (
                      <circle 
                        cx={props?.cx} 
                        cy={props?.cy} 
                        r={6} 
                        fill={color}
                        strokeWidth={2}
                        stroke={color}
                      />
                    );
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Risk Zone Legend */}
      <div className="mt-4 flex items-center justify-center space-x-4 text-xs">
        <div className="flex items-center space-x-2">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: LOAD_RATIO_RISK_BANDS?.LOW_LOAD_RISK?.color }}
          />
          <span className="text-text-secondary">Low load</span>
        </div>
        <div className="flex items-center space-x-2">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: LOAD_RATIO_RISK_BANDS?.LOW?.color }}
          />
          <span className="text-text-secondary">Optimal</span>
        </div>
        <div className="flex items-center space-x-2">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: LOAD_RATIO_RISK_BANDS?.ELEVATED?.color }}
          />
          <span className="text-text-secondary">Elevated</span>
        </div>
        <div className="flex items-center space-x-2">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: LOAD_RATIO_RISK_BANDS?.HIGH?.color }}
          />
          <span className="text-text-secondary">High</span>
        </div>
      </div>
      {/* Wellness Categories Legend */}
      {showWellness && (
        <div className="mt-3 flex items-center justify-center space-x-4 text-xs">
          <span className="text-text-secondary font-medium">Wellness Categories:</span>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22C55E' }} />
            <span className="text-text-secondary">Excellent</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#3B82F6' }} />
            <span className="text-text-secondary">Good</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
            <span className="text-text-secondary">Fair</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#EF4444' }} />
            <span className="text-text-secondary">Poor</span>
          </div>
        </div>
      )}
      {/* Readiness Categories Legend */}
      {showReadiness && (
        <div className="mt-3 flex items-center justify-center space-x-4 text-xs">
          <span className="text-text-secondary font-medium">Readiness Categories:</span>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22C55E' }} />
            <span className="text-text-secondary">Good</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
            <span className="text-text-secondary">Caution</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#EF4444' }} />
            <span className="text-text-secondary">High Risk</span>
          </div>
        </div>
      )}
      {/* Date Range Information */}
      {dateRange && (
        <div className="mt-3 text-center">
          <p className="text-xs text-text-secondary">
            {dateRange?.preset === 'custom' 
              ? `Showing ${dateRange?.startDate} to ${dateRange?.endDate} (${dateRangeDays} days)` 
              : `Showing last ${dateRange?.preset} days`}
            {dateRangeDays >= 30 && ' • 30-day rolling calculations active'}
            {needsScrolling && ' • Scroll horizontally to view full range'}
          </p>
        </div>
      )}
    </div>
  );
};

export default LoadProgressionChart;